import React from "react";
import { FaPlus, FaRegCheckCircle, FaTrash } from "react-icons/fa";
import styled, { css } from "styled-components";

import { IStyle } from "../../Types/Style";
import ActionConfirmationButton from "../../components/pages/CourseAuthor/ActionConfirmation";
import { Link } from "../../components/pages/utils";
import { usePagination } from "../../components/utils/Pagination/hooks";
import {
  BottomPagination,
  Count,
  CustomActionRow,
  TopPagination,
} from "../../components/utils/Pagination/NumberedPagination/common";
import { Table } from "../../components/utils/Pagination/NumberedPagination/table";
import { PageContextProvider } from "../../context/numbered-pagination";
import { pluralize } from "../../utils";
import { AddButton } from "../Common/Components/FormComponents";
import { ICompleteOrganization } from "../types/common";

const CustomAddButton = styled(AddButton)`
  width: 15rem;
`;

export const fixedSize = css`
  text-align: center;
  width: 10rem;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CustomCheck = styled(FaRegCheckCircle)`
  margin-left: 1rem;
`;

interface IThemeListComponentProps {
  addNewTheme: () => void;
  themes: IStyle[];
  deleteTheme: (themeId: string) => void;
  organization: ICompleteOrganization;
}

interface IRow {
  name: React.ReactNode;
  actions: React.ReactNode;
}

const ThemeListComponent: React.FC<IThemeListComponentProps> = props => {
  const { themes } = props;
  const { value, pagedData } = usePagination(themes);
  const total = themes.length;
  const deleteTheme = (styleId: string) => () => props.deleteTheme(styleId);
  const toRow = (style: IStyle): IRow => ({
    actions: <ActionConfirmationButton action="Delete" actionFn={deleteTheme(style.styleId)} ActionIcon={FaTrash} />,
    name: (
      <TitleContainer>
        <Link to={`/theme-management/${style.styleId}`}>{style.displayName}</Link>
        {props.organization.style === style.styleId && <CustomCheck color={"green"} data-testid="selected-theme" />}
      </TitleContainer>
    ),
  });

  return (
    <PageContextProvider value={value}>
      <CustomActionRow>
        <CustomAddButton onClick={props.addNewTheme}>
          <FaPlus /> Create New Theme
        </CustomAddButton>
        <TopPagination total={total} />
        <Count>{pluralize("Theme", themes.length)}</Count>
      </CustomActionRow>
      <Table
        columns={[
          { name: "Name", accessor: "name" },
          { name: "Actions", accessor: "actions", styles: fixedSize },
        ]}
        data={pagedData.map(toRow)}
        emptyDataMessage="No themes for the current organisation"
      />
      <BottomPagination total={total} />
    </PageContextProvider>
  );
};

export default ThemeListComponent;
